import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import merge from "deepmerge";
import { useCallback } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { TopPicksEvents } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";

export const useTracking = (parameters: {
  recommendationId: string;
  topPicksEntityIds: string[];
}) => {
  const { recommendationId, topPicksEntityIds } = parameters;
  const concatenatedEntityIds = topPicksEntityIds.join(",");

  const trackTopPicksWidgetItemClick = (entityId: string, destinationUrl: string) => {
    sendGATracking({
      action: `Top Picks/inHouse/${recommendationId}/Click` as const,
      category: "Top Picks",
      label: entityId,
    });

    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        destination_url: destinationUrl,
        list_of_articles: concatenatedEntityIds,
        page_type: "article",
        recommendation_id: recommendationId,
        widget_name: "top_picks",
      },
      subcategory: "widget",
    });
  };

  const impressionEvent = useCallback<() => TopPicksEvents>(
    () => ({
      action: `Top Picks/inHouse/${recommendationId}/Impression`,
      category: "Top Picks",
      label: concatenatedEntityIds,
    }),
    [recommendationId, concatenatedEntityIds],
  );

  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: concatenatedEntityIds,
        page_type: "article",
        recommendation_id: recommendationId,
        widget_name: "top_picks",
      },
      subcategory: "widget",
    }),
    [recommendationId, concatenatedEntityIds],
  );

  const baseUseTrackImpressionEventByIntersectionParameters = {
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    options: {
      intersectionThreshold: [0.1],
      isSendGA4Tracking: true,
      isSendGATracking: true,
      shouldSendOnce: true,
      shouldThrottle: true,
    },
  };

  const {
    captureTrackImpressionEventTargetElement: captureTopPickWidgetImpressionEventTargetElement,
  } = useTrackImpressionEventByIntersection<TopPicksEvents, RecirculationWidgetImpressionEvent>({
    ...baseUseTrackImpressionEventByIntersectionParameters,
    getEvent: impressionEvent,
    getGa4Event: ga4ImpressionEvent,
  });

  const ga4HeaderSponsorArticleImpressionEvent = (): RecirculationWidgetImpressionEvent => ({
    action: "imp",
    category: "recirculation",
    customized_parameters: {
      page_type: "article",
      widget_name: "sponsor_article",
    },
    subcategory: "widget",
  });

  const {
    captureTrackImpressionEventTargetElement:
      captureHeaderSponsorArticleAdImpressionEventTargetElement,
  } = useTrackImpressionEventByIntersection<TopPicksEvents, RecirculationWidgetImpressionEvent>(
    merge(baseUseTrackImpressionEventByIntersectionParameters, {
      getEvent: null,
      getGa4Event: ga4HeaderSponsorArticleImpressionEvent,
      options: {
        isSendGATracking: false,
      },
    }),
  );

  const {
    captureTrackImpressionEventTargetElement:
      captureFooterSponsorArticleAdImpressionEventTargetElement,
  } = useTrackImpressionEventByIntersection<TopPicksEvents, RecirculationWidgetImpressionEvent>(
    merge(baseUseTrackImpressionEventByIntersectionParameters, {
      getEvent: null,
      getGa4Event: ga4HeaderSponsorArticleImpressionEvent,
      options: {
        isSendGATracking: false,
      },
    }),
  );

  return {
    captureFooterSponsorArticleAdImpressionEventTargetElement,
    captureHeaderSponsorArticleAdImpressionEventTargetElement,
    captureTopPickWidgetImpressionEventTargetElement,
    trackTopPicksWidgetItemClick,
  };
};
